<script>
import axios from '@/utils/axios';
import { clientAdmin } from '@/utils/auth';

export default {
  inject: ['setLoading'],
  data() {
    return {
      salesOrder: {},
      admin: {},
      clientAdmin: clientAdmin(),
    };
  },

  mounted() {
    const public_token = this.$route.params.public_token;
    this.fetchSalesOrder(public_token);
  },

  methods: {

    async fetchSalesOrder(public_token) {
      this.setLoading(true);
      try {
        const response = await axios.get(`sales-orders/${public_token}`);
        this.salesOrder = response.data.data;
        if (this.salesOrder?.admin_id) {
          this.adminDetail(this.salesOrder.admin_id);
        }
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    async adminDetail(admin_id) {
      try {
        const response = await axios.get(`admin/${admin_id}`);
        this.admin = response.data.data;
      } catch (error) {
        console.log(error.message);
      }
    },

    printPage() {
      window.print();
    },
  },
};
</script>

<template>
  <div class="content">

    <!-- Action Buttons (hidden on print) -->
    <div class="d-md-flex d-block align-items-center justify-content-end page-breadcrumb mb-3 mt-3 no-print">
      <div class="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
        <a href="javascript:void(0);" class="btn btn-dark align-items-center" @click="printPage">
          <i class="ti ti-printer me-2"></i>Print
        </a>
      </div>
    </div>

    <!-- Packaging List — Exact Sales Order PDF Layout -->
    <div style="max-width:820px; margin:0 auto; background:#fff; padding:30px; font-family:Arial, sans-serif; font-size:12px; border:1px solid #ddd; box-shadow:0 2px 12px rgba(0,0,0,0.08);">

      <!-- ===== HEADER ===== -->
      <table style="width:100%; border-collapse:collapse; table-layout:fixed; margin-bottom:20px;">
        <tr>
          <!-- Left: Logo + Company Name -->
          <td style="vertical-align:top; width:50%;">
            <img v-if="admin?.company?.logo" :src="$clientLogo(admin.company.logo, admin)" :alt="admin.company?.company_name" style="width:100px; height:auto; object-fit:contain; display:block;">
            <div style="font-size:16px; font-weight:bold; color:#000; margin-top:8px;">{{ admin?.company?.company_name }}</div>
          </td>
          <!-- Right: Packaging List Title -->
          <td style="vertical-align:top; text-align:right; width:50%;">
            <div style="font-size:30px; font-weight:bold; color:#2e7d7d; line-height:1.1;">Packaging List</div>
            <div style="font-size:13px; font-weight:bold; color:#333; margin-top:4px;">Order No# {{ salesOrder.so_number }}</div>
          </td>
        </tr>
      </table>

      <!-- ===== SHIP TO + META INFO ===== -->
      <table style="width:100%; border-collapse:collapse; table-layout:fixed; margin-bottom:20px;">
        <tr>
          <!-- Left: Ship To -->
          <td style="vertical-align:bottom; width:50%; padding-bottom:10px; word-break:break-word;">
            <div style="font-size:11px; color:#555; margin-bottom:6px;">Ship To</div>
            <div style="font-size:15px; font-weight:bold; color:#000; line-height:1.5;">
              {{ salesOrder.shipping_name || salesOrder.name }}
            </div>
            <div style="font-size:12px; color:#333; line-height:1.7; margin-top:4px;">
              <div v-if="salesOrder.shipping_address || salesOrder.address">{{ salesOrder.shipping_address || salesOrder.address }}</div>
              <div>{{ salesOrder.shipping_city || salesOrder.city }}<span v-if="salesOrder.shipping_state || salesOrder.state">, {{ salesOrder.shipping_state || salesOrder.state }}</span> - {{ salesOrder.shipping_pincode || salesOrder.pincode }}</div>
              <div v-if="salesOrder.shipping_country || salesOrder.country">{{ salesOrder.shipping_country || salesOrder.country }}</div>
            </div>
            <div style="font-size:11px; color:#333; margin-top:4px;" v-if="salesOrder.phone">Phone: {{ salesOrder.phone }}</div>
            <div style="font-size:11px; color:#333;" v-if="salesOrder.email">Email: {{ salesOrder.email }}</div>
            <div style="font-size:11px; color:#333;" v-if="salesOrder.gst_no">GSTIN: {{ salesOrder.gst_no }}</div>
          </td>

          <!-- Right: Meta Info -->
          <td style="vertical-align:top; width:50%;">
            <table style="width:100%; border-collapse:collapse; table-layout:fixed;">
              <colgroup>
                <col style="width:45%;">
                <col style="width:55%;">
              </colgroup>
              <tr>
                <td style="padding:3px 6px; text-align:right; color:#555;">Order Date :</td>
                <td style="padding:3px 6px; text-align:right; color:#000;">{{ salesOrder.created_at ? $formatDate(salesOrder.created_at, 'DD-MM-YYYY') : '-' }}</td>
              </tr>
              <tr v-if="salesOrder.shipment_date">
                <td style="padding:3px 6px; text-align:right; color:#555;">Delivery Date :</td>
                <td style="padding:3px 6px; text-align:right; color:#000;">{{ $formatDate(salesOrder.shipment_date, 'DD-MM-YYYY') }}</td>
              </tr>
              <tr>
                <td style="padding:3px 6px; text-align:right; color:#555;">Sales person :</td>
                <td style="padding:3px 6px; text-align:right; color:#000;">{{ salesOrder.created_by?.name }} - {{ salesOrder.created_by?.mobile }}</td>
              </tr>
              <tr v-if="salesOrder.transport_name">
                <td style="padding:3px 6px; text-align:right; color:#555;">Transport :</td>
                <td style="padding:3px 6px; text-align:right; color:#000;">{{ salesOrder.transport_name }}</td>
              </tr>
              <tr v-if="salesOrder.delivery_method">
                <td style="padding:3px 6px; text-align:right; color:#555; vertical-align:top;">Delivery Method :</td>
                <td style="padding:3px 6px; text-align:right; color:#000; word-break:break-word;">{{ salesOrder.delivery_method }}</td>
              </tr>
              <tr v-if="salesOrder.tracking_number">
                <td style="padding:3px 6px; text-align:right; color:#555;">Tracking/LR No :</td>
                <td style="padding:3px 6px; text-align:right; color:#000;">{{ salesOrder.tracking_number }}</td>
              </tr>
            </table>
          </td>
        </tr>
      </table>

      <!-- ===== ITEMS TABLE ===== -->
      <table style="width:100%; border-collapse:collapse;">
        <thead>
          <tr style="background:#2e7d7d;">
            <th style="padding:10px 8px; text-align:left; font-size:12px; color:#fff; width:30px;">#</th>
            <th style="padding:10px 8px; text-align:left; font-size:12px; color:#fff;">Item & Description</th>
            <th style="padding:10px 8px; text-align:left; font-size:12px; color:#fff; width:100px;">HSN/SAC</th>
            <th style="padding:10px 8px; text-align:right; font-size:12px; color:#fff; width:70px;">Qty</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in salesOrder.sales_order_items" :key="item.id || index" style="border-bottom:1px solid #ddd;">
            
            <td style="padding:14px 8px; vertical-align:top; text-align:left; color:#333; font-size:12px; width:30px;">
              {{ index + 1 }}
            </td>

            <td style="padding:14px 8px; vertical-align:top;">
              <table style="width:100%; border-collapse:collapse;">
                <td style="padding:14px 8px; vertical-align:top; word-break:break-word;">
  <div style="font-weight:bold; font-size:13px;">{{ item.item?.code || '-' }}</div>
  <div style="color:#333; line-height:1.8; margin-top:4px; font-size:12px;">
    <div>{{ item.item?.name || item.name }}</div>
    <div v-if="item.description" v-html="item.description"></div>
  </div>
</td>


              </table>
            </td>

            <td style="padding:14px 8px; vertical-align:top; text-align:left; width:100px;">
              {{ item.hsn_code || item.item?.hsn_code || '-' }}
            </td>

            <td style="padding:14px 8px; vertical-align:top; text-align:right; width:70px;">
              <div style="font-weight:bold;">{{ item.quantity }}</div>
              <div style="color:#555; font-size:11px;">{{ item.unit }}</div>
            </td>

          </tr>
        </tbody>
      </table>

      <!-- ===== SIGNATURE ===== -->
      <table style="width:100%; border-collapse:collapse; margin-top:60px;">
        <tr>
          <td style="width:50%; vertical-align:bottom; padding:10px 8px;">
            <div style="width:160px; height:1px; background:#333; margin-bottom:4px;"></div>
            <div style="font-size:11px; font-weight:600; color:#333;">Received By</div>
          </td>
          <td style="width:50%; vertical-align:bottom; padding:10px 8px; text-align:right;">
            <div v-if="admin?.company?.signature" style="margin-bottom:6px;">
              <img :src="$clientSignature(admin.company.signature, admin)" style="height:45px; width:auto; object-fit:contain;">
            </div>
            <div style="width:160px; height:1px; background:#333; margin-bottom:4px; margin-left:auto;"></div>
            <div style="font-size:11px; font-weight:600; color:#333;">Authorised Signatory</div>
            <div style="font-size:10px; color:#666;">For {{ admin?.company?.company_name }}</div>
          </td>
        </tr>
      </table>

    </div>

  </div>
</template>

<style scoped>
@media print {
  .no-print {
    display: none !important;
  }
}
</style>